
import React from 'react';
import { DefaultValues, FormProvider, useForm, FieldValues, Path, PathValue, UseFormSetValue, FieldErrors, UseFormSetFocus } from 'react-hook-form';
import { ZodSchema } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Result, Validator, ValidationErrors } from 'core/infrastructure';
import { FormSchemaContext } from './useFieldSchema';

export type WatchCallback<T, TPath extends Path<T>> = (fieldValue: PathValue<T, TPath>) => void
export type ErrorCallback<T extends FieldValues> = (errors: FieldErrors<T>) => void

export type FormRef<T extends FieldValues> = {
	getValue: () => Promise<Result<T>>;
	setValue: UseFormSetValue<T>;
	setFocus?: UseFormSetFocus<T>;
}

export type FormProps<T extends FieldValues> = React.PropsWithChildren<{
	ref?: React.Ref<FormRef<T>>,
	initialValue: T,
	schema: ZodSchema<T, any, any>,
	validate?: Validator<T>,
	onErrors?: ErrorCallback<T>;
}> & React.FormHTMLAttributes<HTMLFormElement>;

export const Form = <T extends FieldValues,>(
	{ initialValue, validate, children, ref, schema, onErrors, ...formProps }: FormProps<T>
) => {

	const methods = useForm({
		defaultValues: initialValue as DefaultValues<T>,
		resolver: zodResolver(schema),
	});

	const { getValues, trigger, setError, clearErrors, setValue, formState, setFocus } = methods;

	const getValue = React.useCallback(async () => {

		clearErrors();

		const isValid = await trigger();
		if (!isValid) {
			return Result.error();
		}

		// Teoría: sí hay error al parsearlo ha pasado algo muy malo, porque nuestro esquema no es correcto y se supone que
		// las reglas de validación se aplicaron ya con el zodResolver durante el trigger()
		const values = getValues();
		const data = schema.parse(values);

		// Validamos contra el servidor o validaciones que haya introducido el usuario
		const errors = await validate?.(data) || {};
		if (!ValidationErrors.isValid(errors)) {
			Object.keys(errors).forEach(property => setError(property as any, { message: errors[property].join(', ') }));
			return Result.error();
		}

		return Result.ok(data);
	}, [trigger, getValues, schema, validate, setError, clearErrors]);


	React.useEffect(() => {
		// Cuando se actualice el formulario, hay que actualizar también el objeto de errores, 
		// para que el callback de onErrors se ejecute con los nuevos errores esté o no vacío
		onErrors?.(formState.errors);

	}, [formState, onErrors]);

	// Exponemos las funciones que queremos usar desde fuera en el tipo del ref
	React.useImperativeHandle(ref, () => ({
		getValue,
		setValue,
		onErrors,
		setFocus
	}));

	const preventSubmit = React.useCallback((e: React.FormEvent<HTMLFormElement>) => e.preventDefault(), []);

	return (
		<FormSchemaContext.Provider value={schema}>
			<FormProvider {...methods}>
				<form onSubmit={preventSubmit} {...formProps}>
					{children}
				</form>
			</FormProvider>
		</FormSchemaContext.Provider>
	);
};
